import React, { useState } from 'react';
import { Hash, Music, TrendingUp, Search, Filter, Bookmark, ExternalLink, Play } from 'lucide-react';
import Card from '../ui/CardComponent';
import Button from '../ui/ButtonComponent';
import Badge from '../ui/BadgeComponent';

export default function TrendHub() {
  const [activeTab, setActiveTab] = useState<'hashtags' | 'audio' | 'content'>('hashtags');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const trendingHashtags = [
    { tag: '#digitalmarketing', posts: '2.4M', growth: '+12%', category: 'Marketing', score: 95, trend: 'up' },
    { tag: '#contentcreator', posts: '1.8M', growth: '+18%', category: 'Content', score: 92, trend: 'up' },
    { tag: '#socialmedia', posts: '3.1M', growth: '+8%', category: 'Marketing', score: 90, trend: 'up' },
    { tag: '#entrepreneurship', posts: '1.2M', growth: '+15%', category: 'Business', score: 88, trend: 'up' },
    { tag: '#marketingstrategy', posts: '980K', growth: '+10%', category: 'Marketing', score: 87, trend: 'up' },
    { tag: '#branding', posts: '2.2M', growth: '+5%', category: 'Business', score: 85, trend: 'up' },
    { tag: '#influencer', posts: '1.5M', growth: '+20%', category: 'Content', score: 91, trend: 'up' },
    { tag: '#smallbusiness', posts: '890K', growth: '+7%', category: 'Business', score: 83, trend: 'up' },
  ];

  const trendingAudio = [
    { title: 'Summer Vibes Mix 2024', artist: 'DJ Beats', usage: '45.2K', platform: 'Instagram', genre: 'Pop', score: 96 },
    { title: 'Chill Lofi Study Beats', artist: 'LoFi Collective', usage: '38.5K', platform: 'TikTok', genre: 'Lofi', score: 94 },
    { title: 'Motivational Epic Theme', artist: 'Epic Music', usage: '32.1K', platform: 'YouTube', genre: 'Epic', score: 90 },
    { title: 'Upbeat Corporate', artist: 'Studio Tracks', usage: '28.4K', platform: 'LinkedIn', genre: 'Corporate', score: 87 },
    { title: 'Trending Dance Mix', artist: 'Party Beats', usage: '52.8K', platform: 'TikTok', genre: 'Dance', score: 98 },
  ];

  const contentIdeas = [
    { title: 'Behind the Scenes Content', category: 'Video', engagement: 'Very High', difficulty: 'Easy', platforms: ['Instagram', 'TikTok'] },
    { title: 'User Generated Content', category: 'Mixed', engagement: 'High', difficulty: 'Medium', platforms: ['Instagram', 'Facebook'] },
    { title: 'Educational Tutorials', category: 'Video', engagement: 'High', difficulty: 'Medium', platforms: ['YouTube', 'LinkedIn'] },
    { title: 'Product Showcases', category: 'Image', engagement: 'Medium', difficulty: 'Easy', platforms: ['Instagram', 'Facebook'] },
    { title: 'Customer Testimonials', category: 'Video', engagement: 'High', difficulty: 'Easy', platforms: ['LinkedIn', 'Facebook'] },
  ];

  return (
    <div className="max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-semibold text-[#111827]">Trend Hub</h1>
          <div className="flex items-center gap-3">
            <Button variant="secondary" size="md">
              <Filter size={16} className="mr-2" />
              Filter
            </Button>
            <Button variant="primary" size="md">
              <Bookmark size={16} className="mr-2" />
              Saved Trends
            </Button>
          </div>
        </div>
        <p className="text-sm text-[#6B7280]">Discover trending hashtags, audio, and content ideas</p>
      </div>

      {/* Tab Navigation */}
      <Card className="mb-6" padding="none">
        <div className="p-4 flex items-center justify-between border-b border-[#E5E7EB]">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setActiveTab('hashtags')}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                activeTab === 'hashtags'
                  ? 'bg-[#FEE2E2] text-[#DC2626]'
                  : 'text-[#6B7280] hover:bg-[#F9FAFB]'
              }`}
            >
              <Hash size={16} className="inline mr-2" />
              Hashtags
            </button>
            <button
              onClick={() => setActiveTab('audio')}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                activeTab === 'audio'
                  ? 'bg-[#FEE2E2] text-[#DC2626]'
                  : 'text-[#6B7280] hover:bg-[#F9FAFB]'
              }`}
            >
              <Music size={16} className="inline mr-2" />
              Audio Tracks
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
                activeTab === 'content'
                  ? 'bg-[#FEE2E2] text-[#DC2626]'
                  : 'text-[#6B7280] hover:bg-[#F9FAFB]'
              }`}
            >
              <TrendingUp size={16} className="inline mr-2" />
              Content Ideas
            </button>
          </div>

          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={16} />
            <input
              type="text"
              placeholder="Search trends..."
              className="w-full h-10 pl-10 pr-4 text-sm bg-[#F9FAFB] border border-[#E5E7EB] rounded focus:outline-none focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626]"
            />
          </div>
        </div>
      </Card>

      {/* Trending Hashtags */}
      {activeTab === 'hashtags' && (
        <>
          {/* Category Filter */}
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm font-medium text-[#6B7280]">Category:</span>
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-[#FEE2E2] text-[#DC2626]'
                  : 'text-[#6B7280] hover:bg-[#F9FAFB] border border-[#E5E7EB]'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedCategory('marketing')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                selectedCategory === 'marketing'
                  ? 'bg-[#FEE2E2] text-[#DC2626]'
                  : 'text-[#6B7280] hover:bg-[#F9FAFB] border border-[#E5E7EB]'
              }`}
            >
              Marketing
            </button>
            <button
              onClick={() => setSelectedCategory('business')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                selectedCategory === 'business'
                  ? 'bg-[#FEE2E2] text-[#DC2626]'
                  : 'text-[#6B7280] hover:bg-[#F9FAFB] border border-[#E5E7EB]'
              }`}
            >
              Business
            </button>
            <button
              onClick={() => setSelectedCategory('content')}
              className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                selectedCategory === 'content'
                  ? 'bg-[#FEE2E2] text-[#DC2626]'
                  : 'text-[#6B7280] hover:bg-[#F9FAFB] border border-[#E5E7EB]'
              }`}
            >
              Content
            </button>
          </div>

          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#1F2937] text-white text-left text-xs font-medium uppercase tracking-wider">
                    <th className="px-6 py-3">Hashtag</th>
                    <th className="px-6 py-3">Category</th>
                    <th className="px-6 py-3">Total Posts</th>
                    <th className="px-6 py-3">Growth</th>
                    <th className="px-6 py-3">Trend Score</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-[#E5E7EB]">
                  {trendingHashtags.map((hashtag, index) => (
                    <tr key={index} className="hover:bg-[#F9FAFB] transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Hash size={16} className="text-[#6B7280]" />
                          <span className="text-sm font-semibold text-[#111827]">{hashtag.tag}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="default">{hashtag.category}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#111827]">
                        {hashtag.posts}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-green-600">{hashtag.growth}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-[#111827]">{hashtag.score}</span>
                          <TrendingUp size={16} className="text-green-600" />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Button variant="ghost" size="sm">
                          <Bookmark size={14} className="mr-1" />
                          Save
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Trending Audio */}
      {activeTab === 'audio' && (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#1F2937] text-white text-left text-xs font-medium uppercase tracking-wider">
                  <th className="px-6 py-3">Track Title</th>
                  <th className="px-6 py-3">Artist</th>
                  <th className="px-6 py-3">Genre</th>
                  <th className="px-6 py-3">Platform</th>
                  <th className="px-6 py-3">Usage Count</th>
                  <th className="px-6 py-3">Trend Score</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E5E7EB]">
                {trendingAudio.map((audio, index) => (
                  <tr key={index} className="hover:bg-[#F9FAFB] transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#F9FAFB] border border-[#E5E7EB] rounded flex items-center justify-center">
                          <Music size={18} className="text-[#6B7280]" />
                        </div>
                        <span className="text-sm font-medium text-[#111827]">{audio.title}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
                      {audio.artist}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="default">{audio.genre}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="default">{audio.platform}</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#111827]">
                      {audio.usage}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-[#111827]">{audio.score}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <button className="p-2 hover:bg-[#F9FAFB] rounded transition-colors">
                          <Play size={16} className="text-[#6B7280]" />
                        </button>
                        <Button variant="ghost" size="sm">
                          <Bookmark size={14} className="mr-1" />
                          Save
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Content Ideas */}
      {activeTab === 'content' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {contentIdeas.map((idea, index) => (
            <Card key={index} padding="none" hover>
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-base font-semibold text-[#111827] mb-2">{idea.title}</h3>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="default">{idea.category}</Badge>
                      <Badge variant={
                        idea.engagement === 'Very High' ? 'success' :
                        idea.engagement === 'High' ? 'info' : 'default'
                      }>
                        {idea.engagement} Engagement
                      </Badge>
                    </div>
                  </div>
                  <Bookmark size={20} className="text-[#6B7280] cursor-pointer hover:text-[#DC2626] transition-colors" />
                </div>

                <div className="mb-4">
                  <div className="text-xs font-medium text-[#6B7280] mb-2">Best Platforms</div>
                  <div className="flex items-center gap-2">
                    {idea.platforms.map((platform) => (
                      <span key={platform} className="px-2 py-1 bg-[#F9FAFB] text-xs text-[#111827] rounded">
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-[#E5E7EB]">
                  <span className="text-sm text-[#6B7280]">
                    Difficulty: <span className="font-medium text-[#111827]">{idea.difficulty}</span>
                  </span>
                  <Button variant="ghost" size="sm">
                    <ExternalLink size={14} className="mr-1" />
                    View Examples
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
